// Regras das telas Supervisor › Lucro Presumido e Lucro Real.
//
// As duas telas eram cópias de ~2.500 linhas que divergiam só nos detalhes
// abaixo; agora é uma tela só, parametrizada por `CONFIG_REGIME`.

import type { ICompany, IResumoXml } from '../../../../hooks/useCompanies';

export type Regime = 'lucro_presumido' | 'lucro_real';
export type TipoValor = 'guia' | 'calculo' | 'manual';

export interface IImpostoTela {
  key: string;
  rotulo: string;
  /** Tem guia em PDF para abrir no clique. */
  temGuiaPdf: boolean;
  /** Filial não tem guia própria: o card mostra "Empresa filial". */
  soMatriz: boolean;
  /** Mostra o % do imposto sobre o faturamento consolidado. */
  mostraPercentual: boolean;
  /** Explica o card quando não há guia (DIFAL/retenções vêm do Domínio). */
  dica?: string;
  /** Só mostra o valor (sem guia × cálculo × agente): retenções sofridas. */
  informativo?: boolean;
}

export interface IConfigRegime {
  regime: Regime;
  titulo: string;
  /** Sufixo das queryKeys (mantido igual ao das telas antigas). */
  chave: string;
  impostos: IImpostoTela[];
  /** IRPJ/CSLL trimestral: mês 1 e 2 do trimestre o agente mostra prévia. */
  previaTrimestral: boolean;
  /** Tooltip com a fórmula do cálculo agente (presunção do Lucro Presumido). */
  formulaAgente: boolean;
  /**
   * Guia não encontrada é OK quando o cálculo é menor que R$10 (guia abaixo
   * disso não é emitida). No Lucro Presumido a guia ausente segue a regra
   * geral de consistência.
   */
  guiaAusenteOkAbaixoDe10: boolean;
  /**
   * Impostos que o agente não calcula no regime: o card mostra "Não se
   * aplica" (com `dicaAgenteNaoSeAplica`) e compara só guia × cálculo.
   */
  agenteNaoSeAplica?: string[];
  dicaAgenteNaoSeAplica?: string;
  /** Motivos sugeridos ao anotar a empresa. */
  motivos?: string[];
}

const IMPOSTOS_BASE: IImpostoTela[] = [
  { key: 'cofins', rotulo: 'COFINS', temGuiaPdf: true, soMatriz: true, mostraPercentual: true },
  { key: 'csll', rotulo: 'CSLL', temGuiaPdf: true, soMatriz: true, mostraPercentual: true },
  { key: 'icms', rotulo: 'ICMS', temGuiaPdf: true, soMatriz: false, mostraPercentual: true },
  { key: 'irpj', rotulo: 'IRPJ', temGuiaPdf: true, soMatriz: true, mostraPercentual: true },
  { key: 'pis', rotulo: 'PIS', temGuiaPdf: true, soMatriz: true, mostraPercentual: true },
];

// Sem guia em PDF: valores apurados pelo Domínio (extract_impostos).
const IMPOSTOS_DOMINIO: IImpostoTela[] = [
  {
    key: 'difal',
    rotulo: 'DIFAL',
    temGuiaPdf: false,
    soMatriz: false,
    mostraPercentual: false,
    dica: 'Sem guia de DIFAL — dado vem direto do Domínio (extract_impostos). O agente recalcula nota a nota.',
  },
  {
    key: 'retencoes',
    rotulo: 'Retenções',
    temGuiaPdf: false,
    soMatriz: false,
    mostraPercentual: false,
    dica: 'Retenções que a empresa fez nos pagamentos (IRRF, ISS retido, CSRF, INSS) — a recolher, apuradas pelo Domínio (extract_impostos). O agente repete o valor do Domínio.',
  },
  {
    key: 'retencoes_sofridas',
    rotulo: 'Retenções sofridas',
    temGuiaPdf: false,
    soMatriz: false,
    mostraPercentual: false,
    informativo: true,
    dica: 'Retido pelo tomador nas NFS-e prestadas (PIS, COFINS, CSLL, IRRF, INSS), lido dos XML. Abate do imposto a pagar: a guia pode vir sem esse valor. No Domínio essas retenções estão zeradas.',
  },
];

export const CONFIG_REGIME: Record<Regime, IConfigRegime> = {
  lucro_presumido: {
    regime: 'lucro_presumido',
    titulo: 'Lucro Presumido',
    chave: 'lucroPresumido',
    impostos: [...IMPOSTOS_BASE, ...IMPOSTOS_DOMINIO],
    previaTrimestral: true,
    formulaAgente: true,
    guiaAusenteOkAbaixoDe10: false,
  },
  lucro_real: {
    regime: 'lucro_real',
    titulo: 'Lucro Real',
    chave: 'lucroReal',
    impostos: [...IMPOSTOS_BASE, ...IMPOSTOS_DOMINIO],
    previaTrimestral: false,
    formulaAgente: true,
    guiaAusenteOkAbaixoDe10: true,
    agenteNaoSeAplica: ['irpj', 'csll'],
    dicaAgenteNaoSeAplica:
      'No Lucro Real o IRPJ e a CSLL saem do lucro contábil (LALUR), não do faturamento, então o agente não calcula. Confira guia × cálculo do Domínio.',
    motivos: [
      'IRPJ isento (liminar perse)',
      'Empresa com retenções federais',
      'IRPJ/CSLL por quotas',
      'Empresa realiza depósito judicial - PERSE',
      'NFS prestada com retenção de IRRF e CSRF',
      'Divergência devido arredondamentos',
      'Realizado perdcomp no período',
    ],
  },
};

export const formatarEmReais = (valor: number | null | undefined): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor ?? 0);

/** Filial: CNPJ com os dígitos 9-12 diferentes de 0001. */
export const isEmpresaFilial = (cnpj?: string | null): boolean => {
  if (!cnpj) return false;
  return cnpj.replace(/\D/g, '').substring(8, 12) !== '0001';
};

const normalizar = (val: any): number => (val === null || val === undefined ? 0 : Number(val));

/** Tolerância de R$1,00 para considerar valores como iguais. */
const TOLERANCIA = 1.0;
const valoresIguais = (v1: number, v2: number): boolean => Math.abs(v1 - v2) <= TOLERANCIA;

/**
 * IRPJ/CSLL de empresa trimestral em mês intermediário: guia e cálculo são 0
 * (esperado) e o agente mostra a prévia do trimestre — não é erro.
 */
export const isPreviewTrimestral = (
  item: ICompany,
  imposto: string,
  cfg: IConfigRegime,
): boolean => {
  if (!cfg.previaTrimestral) return false;
  if (imposto !== 'irpj' && imposto !== 'csll') return false;
  if (item.periodicidade_irpj_csll === 'mensal') return false;
  return !item.is_fechamento_trimestral;
};

/**
 * Retenção sofrida (XML) que pode abater a guia do imposto: PIS/COFINS no
 * mês, IRRF/CSLL no trimestre do IRPJ/CSLL. Mesma regra do backend
 * (regras_agente_lucro.bate_agente).
 */
export const retencaoDoImposto = (item: ICompany, imposto: string): number => {
  const det = item.calc_manual?.detalhes;
  if (!det) return 0;
  if (imposto === 'pis' || imposto === 'cofins')
    return normalizar(det.retencoes_sofridas?.[imposto]);
  if (imposto === 'irpj') return normalizar(det.retencoes_sofridas_tri?.irrf);
  if (imposto === 'csll') return normalizar(det.retencoes_sofridas_tri?.csll);
  return 0;
};

/** Guia = agente cheio ou agente menos a retenção sofrida. */
const bateAgente = (guia: number, agente: number, retencao: number): boolean =>
  valoresIguais(guia, agente) ||
  (retencao > TOLERANCIA && valoresIguais(guia, Math.max(agente - retencao, 0)));

/** O agente não calcula este imposto no regime (IRPJ/CSLL no Lucro Real). */
export const isAgenteNaoSeAplica = (imposto: string, cfg: IConfigRegime): boolean =>
  !!cfg.agenteNaoSeAplica?.includes(imposto);

/**
 * Um valor (guia, cálculo do Domínio ou cálculo agente) está OK quando bate
 * com pelo menos um dos outros dois. Os três zerados também estão OK.
 */
export const isValueConsistent = (
  item: ICompany,
  imposto: string,
  tipo: TipoValor,
  cfg: IConfigRegime,
): boolean => {
  const guia = normalizar(item.impostos_guia?.[imposto]);
  const calc = normalizar(item.impostos_calculo?.[imposto]);
  const manual = normalizar(item.calc_manual?.[imposto]);

  if (isAgenteNaoSeAplica(imposto, cfg)) {
    return tipo === 'manual' || valoresIguais(guia, calc);
  }

  if (isPreviewTrimestral(item, imposto, cfg)) {
    if (tipo === 'guia' || tipo === 'calculo') return true;
    if (guia === 0 && calc === 0) return true;
  }

  const guiaIgualCalc = valoresIguais(guia, calc);
  const guiaIgualManual = bateAgente(guia, manual, retencaoDoImposto(item, imposto));
  if (guiaIgualCalc && guiaIgualManual) return true;

  const calcIgualManual = valoresIguais(calc, manual);
  switch (tipo) {
    case 'guia':
      return guiaIgualCalc || guiaIgualManual;
    case 'calculo':
      return guiaIgualCalc || calcIgualManual;
    case 'manual':
      return guiaIgualManual || calcIgualManual;
    default:
      return false;
  }
};

/**
 * IRPJ/CSLL devido e sem guia: a guia ainda não saiu — pendência, não erro
 * (KPI "Sem guia IRPJ/CSLL"). Devido = cálculo do Domínio, ou o agente no
 * fechamento do trimestre do Lucro Presumido.
 */
export const isAguardandoGuia = (item: ICompany, imposto: string, cfg: IConfigRegime): boolean => {
  if (imposto !== 'irpj' && imposto !== 'csll') return false;
  if (normalizar(item.impostos_guia?.[imposto]) !== 0) return false;
  const consideraAgente = !isAgenteNaoSeAplica(imposto, cfg) && !!item.is_fechamento_trimestral;
  const esperado = Math.max(
    normalizar(item.impostos_calculo?.[imposto]),
    consideraAgente ? normalizar(item.calc_manual?.[imposto]) : 0,
  );
  return esperado > 10;
};

const plural = (n: number, um: string, varios: string): string => `${n} ${n === 1 ? um : varios}`;

/**
 * Composição da base do agente pelos XML (ver backend
 * services/agente_conferencia.py): linhas para o tooltip.
 */
export const linhasXml = (xml?: IResumoXml | null): { texto: string; alerta?: boolean }[] => {
  if (!xml) return [];
  if (xml.status === 'pendente')
    return [{ texto: 'XMLs ainda em cálculo: por enquanto só o Domínio.' }];
  if (xml.status === 'sem_xml')
    return [{ texto: 'Sem XMLs no mês: o agente usa o Domínio.', alerta: true }];
  const linhas: { texto: string; alerta?: boolean }[] = [];
  const qtd = xml.qtd || {};
  const valores = xml.valores || {};
  if (qtd.so_xml)
    linhas.push({
      texto: `+ ${plural(qtd.so_xml, 'nota só no XML', 'notas só no XML')} (fora do Domínio): ${formatarEmReais(valores.so_xml)}`,
      alerta: true,
    });
  if (qtd.cancelada_so_xml)
    linhas.push({
      texto: `− ${plural(qtd.cancelada_so_xml, 'nota cancelada', 'notas canceladas')} no XML e ativa no Domínio: ${formatarEmReais(Math.abs(valores.cancelada_so_xml ?? 0))}`,
      alerta: true,
    });
  if (qtd.valor_divergente)
    linhas.push({
      texto: `${plural(qtd.valor_divergente, 'NFS-e', 'NFS-e')} com valor diferente do XML (não muda a base: o Domínio pode lançar só a receita própria)`,
      alerta: true,
    });
  if (!linhas.length) linhas.push({ texto: 'XMLs conferidos: batem com o Domínio.' });
  return linhas;
};

const DATA_CURTA = (iso: string | null): string => {
  if (!iso) return '';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano.slice(2)}`;
};

const MOTIVO_NOTA = {
  so_xml: 'só no XML',
  cancelada_so_xml: 'cancelada no XML',
  valor_divergente: 'valor diferente',
};

/** Notas que mais pesam, para o tooltip (o resto só conta). */
export const notasXmlResumo = (xml?: IResumoXml | null, max = 4): string[] =>
  (xml?.notas || []).slice(0, max).map(n => {
    const valor = n.motivo === 'valor_divergente' ? n.valor_xml : Math.abs(n.ajuste);
    return `${(n.tipo || 'nota').toUpperCase()} ${n.numero ?? ''} · ${DATA_CURTA(n.data)} · ${formatarEmReais(valor)} (${MOTIVO_NOTA[n.motivo]})`;
  });

/**
 * Possíveis motivos de um imposto que não bateu — o que conferir, mesmo
 * quando o sistema não sabe a causa exata. Olha a direção da diferença
 * (guia acima/abaixo do agente ou do cálculo) e o que se sabe da empresa
 * (XML, regime, venda de mercadoria, retenção, filial). Sempre devolve ao
 * menos um item, para ninguém ficar sem pista.
 */
export const possiveisMotivos = (item: ICompany, imposto: string, cfg: IConfigRegime): string[] => {
  const guia = normalizar(item.impostos_guia?.[imposto]);
  const calc = normalizar(item.impostos_calculo?.[imposto]);
  const agente = normalizar(item.calc_manual?.[imposto]);
  const det = item.calc_manual?.detalhes;
  const xml = det?.xml;
  const lr = cfg.regime === 'lucro_real';
  const temSaida = normalizar(item.impostos_calculo?.vl_saida) > 0;
  const naoSeAplica = isAgenteNaoSeAplica(imposto, cfg);
  // referência: o agente; onde ele não se aplica, o cálculo do Domínio
  const ref = naoSeAplica ? calc : agente;
  const guiaMenor = guia > 0 && guia < ref - TOLERANCIA;
  const guiaMaior = guia > ref + TOLERANCIA;
  const m: string[] = [];

  if (guia === 0 && ref > 10) {
    m.push(
      'Sem guia: ainda não emitida, paga com outro código/competência, compensada (PER/DCOMP) ou o robô não achou o PDF na pasta da empresa.',
    );
  }

  // XML x Domínio: a base pode estar errada no Domínio
  if (xml?.status === 'xml' && xml.qtd?.so_xml)
    m.push(
      'Há notas no XML que o Domínio não lançou: o agente soma, a guia e o cálculo do Domínio não. Confira na Conferência Fiscal se a nota é da empresa e do mês.',
    );
  if (xml?.status === 'xml' && xml.qtd?.cancelada_so_xml)
    m.push(
      'Há notas canceladas no XML que o Domínio ainda conta como ativas: o agente tira, o Domínio não. Cancele a nota no Domínio se o cancelamento for real.',
    );
  if (xml?.status === 'xml' && xml.qtd?.valor_divergente)
    m.push(
      'Há NFS-e com valor diferente entre XML e Domínio: confira qual está certo (intermediação lança só a comissão).',
    );
  if (xml?.status === 'sem_xml')
    m.push(
      'Sem XMLs no mês: o agente usa só o Domínio, então nota faltando ou cancelada não aparece aqui. Verifique se as notas estão subindo para o sistema.',
    );

  if (imposto === 'pis' || imposto === 'cofins') {
    if (isEmpresaFilial(item.cnpj))
      m.push(
        'Filial: o agente usa o faturamento da raiz (matriz + filiais); a guia sai na matriz.',
      );
    if (lr) {
      if (calc === 0 && guia > 0)
        m.push('Cálculo da importação zerado (comum no Lucro Real): compare guia × agente.');
      if (guiaMenor)
        m.push(
          'Guia menor que o agente: compensação (PER/DCOMP), retenção sofrida que não está no XML, crédito extemporâneo ou crédito maior que o do Domínio.',
        );
      if (guiaMaior)
        m.push(
          'Guia maior que o agente: receita financeira (no Lucro Real paga PIS 0,65% / COFINS 4%), outras receitas tributáveis, crédito do Domínio maior do que o usado na guia ou multa/juros.',
        );
      if (Math.abs(agente - calc) > TOLERANCIA && calc > 0)
        m.push(
          'Agente diferente do cálculo: o crédito vem do Domínio; receita com alíquota zero, monofásico, exportação ou suspensão não sai da base do agente.',
        );
    } else {
      if (temSaida && (guiaMenor || agente > calc + TOLERANCIA))
        m.push(
          'Venda de mercadoria: produtos monofásicos, alíquota zero ou ST reduzem o PIS/COFINS; o agente aplica 0,65% / 3% sobre todo o faturamento.',
        );
      if (guiaMenor && retencaoDoImposto(item, imposto) <= TOLERANCIA)
        m.push('Guia menor: retenção sofrida não informada no XML ou compensação (PER/DCOMP).');
      if (guiaMaior)
        m.push(
          'Guia maior: receita fora do faturamento do Domínio (acumulador sem "compõe faturamento"), débito de outro mês ou multa/juros.',
        );
    }
  }

  if ((imposto === 'irpj' || imposto === 'csll') && naoSeAplica) {
    m.push(
      'Lucro Real: IRPJ/CSLL saem do lucro (LALUR). Guia × cálculo diferentes: estimativa mensal x balancete de suspensão, compensação de prejuízo/base negativa, retenções abatidas ou PER/DCOMP.',
    );
  } else if (imposto === 'irpj' || imposto === 'csll') {
    if (guiaMenor)
      m.push(
        'Guia menor que o agente: IRRF/CSLL retidos abatidos (nem sempre estão no XML), PERSE/isenção, atividade com outro percentual de presunção (1,6% / 8% / 16% / 32%) ou pagamento em quotas.',
      );
    if (guiaMaior)
      m.push(
        'Guia maior que o agente: receita financeira ou ganho de capital somado na guia, adicional de 10% de meses anteriores, majoração da LC 224 ou multa/juros.',
      );
    if (isEmpresaFilial(item.cnpj)) m.push('Filial não apura IRPJ/CSLL: a guia sai na matriz.');
  }

  if (imposto === 'icms') {
    m.push(
      'ICMS: crédito das entradas (vem do Domínio), saldo credor do mês anterior, ST/DIFAL somados na mesma guia ou benefício fiscal (redução de base, crédito presumido).',
    );
    if (det?.icms_ajuste_xml)
      m.push('Há ICMS de notas que o Domínio não reflete somado no agente (ver tooltip).');
  }

  if (!m.length)
    m.push(
      'O sistema não achou a causa. Confira: guia do mês/código certo, valor com multa/juros, retificação no Domínio depois da importação ou lançamento manual.',
    );
  return m;
};

/** Guia não encontrada: OK ou erro, conforme a regra do regime. */
export const guiaAusenteOk = (item: ICompany, imposto: string, cfg: IConfigRegime): boolean =>
  cfg.guiaAusenteOkAbaixoDe10
    ? normalizar(item.impostos_calculo?.[imposto]) < 10
    : isValueConsistent(item, imposto, 'guia', cfg);

/** Ícone cinza neutro: cálculo de IRPJ/CSLL zerado na prévia trimestral (esperado). */
export const isCalculoNeutro = (item: ICompany, imposto: string, cfg: IConfigRegime): boolean =>
  isPreviewTrimestral(item, imposto, cfg) && normalizar(item.impostos_calculo?.[imposto]) === 0;

export const textoPreviaTrimestral = (item: ICompany): string => {
  const mes = item.mes_no_trimestre || 1;
  return `Prévia trimestral (${mes} de 3 meses). IRPJ e CSLL são apurados trimestralmente. Este valor considera o faturamento acumulado até o mês atual. O cálculo definitivo será no 3º mês do trimestre.`;
};

/** Texto do alerta de receita financeira marcada como faturamento no Domínio. */
export const textoReceitaFinanceira = (
  rf: IReceitasFinanceiras,
  regime: Regime = 'lucro_presumido',
): string => {
  const acumuladores = rf.acumuladores
    .map(a => `${a.codigo ?? '—'} ${a.nome ?? ''}`.trim())
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .join(', ');
  const fora = rf.total - rf.descontado;
  const irpj =
    regime === 'lucro_presumido'
      ? ' No IRPJ/CSLL do cálculo agente ela entra 100% na base (sem presunção).'
      : '';
  const base = `O Domínio soma receita financeira no faturamento (acumulador ${acumuladores}, marcado como "compõe faturamento"). Nesta tela ${formatarEmReais(
    rf.descontado,
  )} foi retirado do faturamento e do PIS/COFINS do cálculo agente.${irpj}`;
  return fora > 0.005
    ? `${base} ${formatarEmReais(fora)} não estava no faturamento do mês, então não houve o que descontar.`
    : base;
};

/** Receita financeira que o Domínio já deixa fora do faturamento (cadastro certo). */
export const textoReceitaFinanceiraFora = (
  rf: { total: number; acumuladores: { codigo: number | null; nome: string | null }[] },
  regime: Regime,
): string => {
  const acumuladores = rf.acumuladores
    .map(a => `${a.codigo ?? '—'} ${a.nome ?? ''}`.trim())
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .join(', ');
  const base = `Acumulador ${acumuladores}, fora do faturamento no Domínio (cadastro certo): não entra no faturamento nem no PIS/COFINS desta tela.`;
  return regime === 'lucro_presumido'
    ? `${base} No Lucro Presumido ela entra 100% na base do IRPJ/CSLL (sem presunção), já somada no cálculo agente.`
    : base;
};

export interface IMotivo {
  chave: 'difal' | 'receita_financeira_irpj';
  /** Impostos cuja divergência o motivo explica. */
  impostos: string[];
  titulo: string;
  texto: string;
}

const notas = (n: number): string => `${n} ${n === 1 ? 'nota' : 'notas'}`;

/**
 * Motivo provável das divergências com o Domínio (vem do backend, ver
 * app/services/motivos_divergencia.py). Só explica; não muda nenhum valor.
 */
export const motivosDivergencia = (item: ICompany): IMotivo[] => {
  const m = item.motivos_divergencia;
  if (!m) return [];
  const motivos: IMotivo[] = [];

  if (m.difal) {
    const d = m.difal;
    const entrada = d.qtd_entrada
      ? ` (${d.qtd_entrada} de entrada de outro estado: uso/consumo ou ativo)`
      : '';
    const partes = [
      `O agente calculou ${formatarEmReais(d.agente)} em ${notas(
        d.qtd_notas,
      )}${entrada}; o Domínio apurou ${formatarEmReais(d.dominio)}.`,
    ];
    if (d.qtd_sem_icms_destacado) {
      partes.push(
        `${notas(d.qtd_sem_icms_destacado)} sem ICMS destacado: o agente usa o valor da nota.`,
      );
    }
    if (d.sem_inscricao_estadual) {
      partes.push(
        'Empresa sem inscrição estadual: como não contribuinte, o DIFAL da compra costuma ser recolhido pelo remetente.',
      );
    }
    partes.push('Confirme com o fiscal se o DIFAL é devido.');
    motivos.push({
      chave: 'difal',
      impostos: ['difal'],
      titulo: d.dominio ? 'DIFAL diferente do Domínio' : 'DIFAL só no cálculo agente',
      texto: partes.join(' '),
    });
  }

  if (m.receita_financeira_irpj) {
    const r = m.receita_financeira_irpj;
    motivos.push({
      chave: 'receita_financeira_irpj',
      impostos: ['irpj', 'csll'],
      titulo: 'Domínio não somou a receita financeira no IRPJ/CSLL',
      texto: `O agente soma ${formatarEmReais(
        r.agente,
      )} de receita financeira 100% na base (Lei 9.430/96, art. 25, II); no IRPJ do Domínio essa parcela está ${formatarEmReais(
        r.dominio,
      )}. Verifique o acumulador de receita financeira no Domínio.`,
    });
  }
  return motivos;
};

export interface IReceitasFinanceiras {
  total: number;
  descontado: number;
  acumuladores: { codigo: number | null; nome: string | null; valor: number }[];
}
